import React, { useRef, useState } from "react";
import { Box, Container, Stack, Typography, MenuItem, Button, Paper, MenuList, Popper, Grow, useMediaQuery, Alert, Divider, ListSubheader, IconButton } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useTheme, alpha } from "@mui/material/styles";
import { ReactComponent as ProjectLogo } from "../../assets/icons/project-logo.svg";
import { ReactComponent as NetworkIcon } from "../../assets/icons/network.svg";
import { GiCompass } from "react-icons/gi";

import "./Navbar.sass";

import { useApp } from "../../hooks/useApp";
import AddIcon from "@mui/icons-material/Add";
import LanIcon from "@mui/icons-material/Lan";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { AddNetworkModal } from "../modals/add-network-modal/AddNetworkModal";
import { RemoveNetworkModal } from "../modals/remove-network-modal/RemoveNetworkModal";

export const Navbar = () => {
  const app = useApp();
  const theme = useTheme();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState<"add-network" | "remove-network" | null>(null);
  const [idToRemove, setIdToRemove] = useState<number>(0);

  const handleChange = (networkId: number) => {
    setTimeout(() => {
      app.selectNetwork(networkId);
    }, 200);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  function handleRemove(e: any, id: number) {
    e.stopPropagation();
    setOpen(false);
    setIdToRemove(id);
    setOpenModal("remove-network");
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const standardNetworks = app.networks.filter((network) => network.type === "standard");
  const customNetworks = app.networks.filter((network) => network.type === "custom");

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bgcolor: theme.palette.background.default,
          top: 0,
          width: "100%",
          py: isMdDown ? 1 : 2,
          zIndex: 9000,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        }}
      >
        <Container maxWidth={"md"}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{ width: "100%" }}>
            <Stack flexDirection={"row"} justifyContent={"start"} alignItems={"center"} gap={1}>
              <GiCompass style={{ fontSize: "30px" }} />
              <Typography variant={"h3"} component={"span"} sx={{ fontWeight: 700 }}>
                Casper Compass
              </Typography>
            </Stack>

            <div>
              <Button
                ref={anchorRef}
                id="composition-button"
                aria-controls={open ? "composition-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                disableRipple
                sx={{
                  background: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  textTransform: "none",
                  textAlign: "left",
                }}
                aria-label="Network"
              >
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                  {app.network.type === "standard" && <NetworkIcon fill={app.network.name === "mainnet" ? "#ff0000" : "#00bba4"} />}
                  {app.network.type === "custom" && <LanIcon color={"inherit"} />}
                  <Stack>
                    <Typography sx={{ fontSize: "0.8rem", textTransform: app.network.type === "standard" ? "uppercase" : "none" }}>{app.network.name}</Typography>
                    <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>{app.network.address}</Typography>
                  </Stack>
                </Stack>
              </Button>
              <Popper open={open} anchorEl={anchorRef.current} role={undefined} placement="bottom-end" transition disablePortal>
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === "bottom-start" ? "left top" : "left bottom",
                    }}
                  >
                    <Paper sx={{ background: theme.palette.background.paper, maxHeight: "calc(90vh - 70px)", overflow: "auto" }}>
                      <ClickAwayListener onClickAway={handleClose}>
                        <Box>
                          <MenuList //
                            autoFocusItem={open}
                            onKeyDown={handleListKeyDown}
                            sx={{ mt: 0.5 }}
                          >
                            {standardNetworks.map((network) => {
                              let color = "inherit";
                              if (network.name === "mainnet") color = "#ff0000";
                              if (network.name === "testnet") color = "#00bba4";
                              return (
                                <MenuItem key={network.id} onClick={() => handleChange(network.id)}>
                                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                                    <NetworkIcon fill={color} />
                                    <Stack>
                                      <Typography sx={{ fontSize: "0.8rem" }}>{network.name.toUpperCase()}</Typography>
                                      <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>{network.address}</Typography>
                                    </Stack>
                                  </Stack>
                                </MenuItem>
                              );
                            })}
                          </MenuList>

                          <MenuList //
                            autoFocusItem={open}
                            onKeyDown={handleListKeyDown}
                            sx={{ mt: 0.5 }}
                            subheader={
                              <ListSubheader component="div" sx={{ fontWeight: 300, fontSize: "0.8rem" }}>
                                Custom networks
                              </ListSubheader>
                            }
                          >
                            {customNetworks.length === 0 && (
                              <Typography textAlign={"center"} sx={{ fontWeight: 300, fontSize: "0.8rem" }}>
                                No custom networks added
                              </Typography>
                            )}
                            {customNetworks.length > 0 &&
                              customNetworks.map((network) => {
                                return (
                                  <MenuItem key={network.id} onClick={() => handleChange(network.id)} sx={{ "&:hover .remove-network": { opacity: 1 } }}>
                                    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{ width: "100%" }}>
                                      <LanIcon color={"inherit"} />
                                      <Stack>
                                        <Typography sx={{ fontSize: "0.8rem" }}>{network.name}</Typography>
                                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>{network.address}</Typography>
                                      </Stack>
                                      <IconButton color="error" sx={{ ml: "auto", opacity: 0 }} onClick={(e) => handleRemove(e, network.id)} className="remove-network">
                                        <HighlightOffIcon />
                                      </IconButton>
                                    </Stack>
                                  </MenuItem>
                                );
                              })}
                            <MenuItem sx={{ mt: "1rem" }}>
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                color={"primary"}
                                fullWidth
                                onClick={() => {
                                  setOpen(false);
                                  setTimeout(() => {
                                    setOpenModal("add-network");
                                  }, 200);
                                }}
                              >
                                Add custom network
                              </Button>
                            </MenuItem>
                          </MenuList>
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </div>
          </Stack>
        </Container>
      </Box>

      <AddNetworkModal open={openModal === "add-network"} setOpen={setOpenModal} />
      <RemoveNetworkModal open={openModal === "remove-network"} setOpen={setOpenModal} id={idToRemove} />
    </>
  );
};
